import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Target, Plus, CheckCircle2, Circle, Clock, Sparkles,
  ArrowLeft, Trash2, ChevronRight, Loader2, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  franchise: { label: 'Franchise', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  debt_clearance: { label: 'Debt Clearance', color: 'text-red-600 bg-red-50 border-red-200' },
  new_location: { label: 'New Location', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  cost_reduction: { label: 'Cost Reduction', color: 'text-green-600 bg-green-50 border-green-200' },
  staffing: { label: 'Staffing', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  custom: { label: 'Custom', color: 'text-gray-600 bg-gray-50 border-gray-200' },
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Low', color: 'bg-green-100 text-green-700 border-green-200' },
};

export default function ProjectsPage() {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAICreate, setShowAICreate] = useState(false);
  const [goal, setGoal] = useState('');
  const [manualForm, setManualForm] = useState({ title: '', type: 'custom', description: '', visibility: 'shared' });
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium' });
  const [credits, setCredits] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setProjects(data);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchCredits = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ai-credits`, { headers: { Authorization: `Bearer ${token}` } });
      setCredits(await res.json());
    } catch {}
  }, [token]);

  const fetchProjectDetail = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedProject(await res.json());
    } catch { toast.error('Failed to load project'); }
  }, [token]);

  useEffect(() => { fetchProjects(); fetchCredits(); }, [fetchProjects, fetchCredits]);

  const generateProject = async () => {
    if (!goal.trim()) { toast.error('Describe your goal'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/projects/generate`, {
        method: 'POST', headers, body: JSON.stringify({ goal }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const project = await res.json();
      toast.success(`Created "${project.title}" with ${project.task_count} tasks`);
      setGoal('');
      setShowAICreate(false);
      fetchProjects();
      fetchCredits();
      setSelectedProject(project);
    } catch (e) { toast.error(e.message || 'Failed to generate project'); }
    finally { setGenerating(false); }
  };

  const createManual = async () => {
    if (!manualForm.title.trim()) { toast.error('Title required'); return; }
    try {
      const res = await fetch(`${API}/api/projects`, {
        method: 'POST', headers, body: JSON.stringify(manualForm),
      });
      const project = await res.json();
      toast.success('Project created');
      setManualForm({ title: '', type: 'custom', description: '', visibility: 'shared' });
      setShowCreate(false);
      fetchProjects();
      setSelectedProject(project);
    } catch { toast.error('Failed to create project'); }
  };

  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await fetch(`${API}/api/projects/${selectedProject.id}/tasks/${taskId}`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus }),
      });
      fetchProjectDetail(selectedProject.id);
    } catch { toast.error('Failed to update task'); }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await fetch(`${API}/api/projects/${selectedProject.id}/tasks`, {
        method: 'POST', headers, body: JSON.stringify(newTask),
      });
      setNewTask({ title: '', priority: 'medium' });
      fetchProjectDetail(selectedProject.id);
    } catch { toast.error('Failed to add task'); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project and all its tasks?')) return;
    try {
      await fetch(`${API}/api/projects/${id}`, { method: 'DELETE', headers });
      toast.success('Project deleted');
      setSelectedProject(null);
      fetchProjects();
    } catch { toast.error('Failed to delete'); }
  };

  const updateProjectStatus = async (status) => {
    try {
      await fetch(`${API}/api/projects/${selectedProject.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status }),
      });
      fetchProjectDetail(selectedProject.id);
      fetchProjects();
    } catch { toast.error('Failed to update'); }
  };

  // ─── Detail View ───
  if (selectedProject) {
    const typeCfg = TYPE_CONFIG[selectedProject.type] || TYPE_CONFIG.custom;
    return (
      <div className="space-y-6" data-testid="project-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)} data-testid="back-to-projects">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold">{selectedProject.title}</h1>
              <Badge variant="outline" className={typeCfg.color}>{typeCfg.label}</Badge>
            </div>
            {selectedProject.description && (
              <p className="text-muted-foreground">{selectedProject.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedProject.status === 'active' && (
              <Button variant="outline" size="sm" onClick={() => updateProjectStatus('completed')} data-testid="complete-project-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteProject(selectedProject.id)} data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Progress value={selectedProject.progress_pct} className="flex-1 h-3" />
          <span className="text-sm font-medium text-muted-foreground">
            {selectedProject.completed_count}/{selectedProject.task_count} tasks
          </span>
        </div>

        <Separator />

        {/* Add Task */}
        <div className="flex gap-2" data-testid="add-task-form">
          <Input
            placeholder="Add a task..."
            value={newTask.title}
            onChange={(e) => setNewTask(t => ({ ...t, title: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
            data-testid="new-task-input"
          />
          <Select value={newTask.priority} onValueChange={(v) => setNewTask(t => ({ ...t, priority: v }))}>
            <SelectTrigger className="w-28" data-testid="new-task-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={addTask} data-testid="add-task-btn"><Plus className="w-4 h-4" /></Button>
        </div>

        {/* Task List */}
        <div className="space-y-2" data-testid="task-list">
          {(selectedProject.tasks || []).map((task) => {
            const done = task.status === 'completed';
            const priCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
            return (
              <div
                key={task.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${done ? 'opacity-60 bg-muted/30' : 'bg-card hover:bg-accent/30'}`}
                data-testid={`task-${task.id}`}
              >
                <button
                  onClick={() => toggleTask(task.id, task.status)}
                  className="mt-0.5 shrink-0"
                  data-testid={`toggle-task-${task.id}`}
                >
                  {done
                    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : <Circle className="w-5 h-5 text-muted-foreground hover:text-primary" />
                  }
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>
                      {task.title}
                    </span>
                    <Badge variant="outline" className={`text-[10px] h-4 ${priCfg.color}`}>{priCfg.label}</Badge>
                    {task.source === 'ai_generated' && (
                      <Sparkles className="w-3 h-3 text-purple-500" />
                    )}
                  </div>
                  {task.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{task.description}</p>
                  )}
                </div>
                {task.linked_page && (
                  <a href={task.linked_page} className="text-xs text-primary hover:underline shrink-0">
                    View <ChevronRight className="w-3 h-3 inline" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Projects List View ───
  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground text-sm">Track business goals with AI-generated action plans</p>
        </div>
        <div className="flex items-center gap-2">
          {credits && (
            <div className="text-xs text-muted-foreground mr-2" data-testid="credits-display">
              <Zap className="w-3 h-3 inline text-amber-500" /> {credits.remaining}/{credits.allowance} credits
            </div>
          )}
          <Dialog open={showAICreate} onOpenChange={setShowAICreate}>
            <DialogTrigger asChild>
              <Button data-testid="ai-generate-btn">
                <Sparkles className="w-4 h-4 mr-1" /> AI Generate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Generate Project with AI</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Describe your goal</Label>
                  <Textarea
                    placeholder="e.g. I want to franchise the cafe next year..."
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    rows={4}
                    data-testid="goal-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    AI will analyse your business data and create specific tasks (uses 5 credits)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={generateProject} disabled={generating} data-testid="generate-btn">
                  {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
                    : <><Sparkles className="w-4 h-4 mr-1" /> Generate Project</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="manual-create-btn">
                <Plus className="w-4 h-4 mr-1" /> Manual
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Title</Label>
                  <Input value={manualForm.title} onChange={(e) => setManualForm(f => ({ ...f, title: e.target.value }))} data-testid="manual-title-input" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={manualForm.type} onValueChange={(v) => setManualForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="manual-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea value={manualForm.description} onChange={(e) => setManualForm(f => ({ ...f, description: e.target.value }))} rows={2} />
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select value={manualForm.visibility} onValueChange={(v) => setManualForm(f => ({ ...f, visibility: v }))}>
                    <SelectTrigger data-testid="manual-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shared">Shared — visible to all team members</SelectItem>
                      <SelectItem value="personal">Personal — only visible to you</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createManual} data-testid="create-project-btn">Create Project</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-medium mb-1">No projects yet</h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
              Create a project manually or let AI generate a plan from your business goal
            </p>
            <Button onClick={() => setShowAICreate(true)} data-testid="empty-state-generate">
              <Sparkles className="w-4 h-4 mr-1" /> Generate your first project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => {
            const typeCfg = TYPE_CONFIG[p.type] || TYPE_CONFIG.custom;
            return (
              <Card
                key={p.id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fetchProjectDetail(p.id)}
                data-testid={`project-card-${p.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={`text-xs ${typeCfg.color}`}>{typeCfg.label}</Badge>
                    <div className="flex items-center gap-1">
                      {p.visibility === 'personal' && (
                        <Badge variant="outline" className="text-[10px] h-4 text-muted-foreground">Personal</Badge>
                      )}
                      {p.status === 'completed' && (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Done</Badge>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-base leading-tight mt-1">{p.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Progress value={p.progress_pct} className="h-2 mb-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{p.completed_count}/{p.task_count} tasks</span>
                    <span>{p.progress_pct}%</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
